/**
 * customer-bootstrap · osDebug 红字日志面板
 *
 * 来源：index.html 内联脚本 #4（已迁移）。
 *
 * 行为契约（必须与原 inline IIFE 完全一致）：
 *   - 默认完全沉默，仅在 URL 带 `?osDebug=1` 时启用
 *   - 监听 window 'error'（capture 阶段，含资源加载错误）+ 'unhandledrejection'
 *   - 在 <body> 底部 fixed 位置挂载 #os-client-debug-log（最多 120 行）
 *   - 调用方 `bootstrap()` 必须**最早**调用，确保 listener 早于业务脚本注册
 *
 * 注意：
 *   - 单次调用守卫：重复调用是 no-op（防止 bundle 双载/HMR 时重复挂监听）
 *   - 整个函数体外层 try-catch 兜底，调试代码自身崩溃绝不影响业务
 */

import { getQueryParam } from './utils';

const MAX_LINES = 120;
const PANEL_ID = 'os-client-debug-log';
const PANEL_CLASS =
  'fixed bottom-0 left-0 right-0 z-[99999] max-h-[35vh] overflow-y-auto whitespace-pre-wrap break-all border-t border-red-500/40 bg-neutral-950/95 px-2 py-1.5 font-mono text-[11px] leading-snug text-red-100 shadow-[0_-4px_24px_rgba(0,0,0,.45)] dark:bg-black/95';

let installed = false;

const formatErrorBits = (e: ErrorEvent): string => {
  const bits: string[] = [];
  if (e.message) bits.push(String(e.message));

  /* 资源加载错误：e.target 是失败的 <img>/<script>/<link> 等 */
  const t = e.target as (HTMLElement & { src?: string; href?: string }) | null;
  if (t && t !== (document as unknown as EventTarget) && t !== (window as unknown as EventTarget)) {
    if (t.src) bits.push(`src=${t.src}`);
    else if (t.href) bits.push(`href=${t.href}`);
    if (t.tagName) bits.push(`tag=${t.tagName}`);
  }

  if (e.filename) bits.push(`script:${e.filename}:${e.lineno ?? 0}`);
  if (e.error?.message) bits.push(`Error:${e.error.message}`);
  if (!bits.length) bits.push('(无 message，多为外链脚本/字体/CSS/图片加载失败)');
  return bits.join(' | ');
};

export const initDebugLog = (): void => {
  if (installed) return;
  try {
    if (getQueryParam('osDebug') !== '1') return;
    installed = true;

    const lines: string[] = [];

    const log = (msg: string): void => {
      const t = new Date().toISOString().slice(11, 23);
      lines.push(`[${t}] ${msg}`);
      if (lines.length > MAX_LINES) lines.splice(0, lines.length - MAX_LINES);
      const el = document.getElementById(PANEL_ID);
      if (el) {
        el.textContent = lines.join('\n');
        el.scrollTop = el.scrollHeight;
      }
    };

    /* capture=true 才能拿到资源加载失败（不冒泡） */
    window.addEventListener(
      'error',
      (e) => {
        log(formatErrorBits(e));
        if (e.error?.stack) log(String(e.error.stack).slice(0, 800));
      },
      true,
    );

    window.addEventListener('unhandledrejection', (e) => {
      const r = e.reason as { message?: unknown } | null | undefined;
      const part = r?.message != null ? String(r.message) : String(r);
      log(`Promise: ${part}`);
    });

    const mount = (): void => {
      if (document.getElementById(PANEL_ID)) return;
      const d = document.createElement('div');
      d.id = PANEL_ID;
      d.className = PANEL_CLASS;
      d.setAttribute('role', 'log');
      d.setAttribute('aria-label', '客户端调试日志');
      document.body.appendChild(d);
      log('osDebug 已开启（?osDebug=1）— 脚本错误将显示在此处');
    };

    if (document.body) mount();
    else document.addEventListener('DOMContentLoaded', mount);
  } catch {
    /* 调试模块自身崩溃绝不影响业务 */
  }
};
